import { getVocabulariesDataList } from '../api/vocabulary';
import { getUsersCustom } from '../api/users';
import { GET_ALL_QUERY_PARAMS } from '../constants';
import { USER_TYPES_VALUES } from '../constants/forms';
import { defaultValues } from '../forms/ProjectFormContainer/ProjectFormLabels';
import { LeadRequester } from '../types';

// Shape of GET /api/v1/epic/project-imports/ rows after camelCase conversion.
export type ProjectImportLead = {
  id: number;
  projectNumber: string;
  projectName: string;
  projectType: string;
  street: string;
  street2?: string | null;
  city: string;
  state: string;
  zipCode: string;
  jurisdiction: number;
  jurisdictionName: string;
  assignedEmployee: number | null;
  assignedEmployeeFullName: string | null;
  assignedEmployeeName: string;
  requester: LeadRequester;
  permits: { name: string; serviceType: string; work: string; inspections?: number }[];
  fees: { description: string; amount: string; permitName?: string | null }[];
  folderUrl: string;
  signedPdfUrl: string;
  signedAt: string;
  approvalType: string;
  createdAt: string;
};

const VOCAB_BUILDING = 1;
const VOCAB_WORK = 2;
const VOCAB_SERVICE_TYPE = 5;

const sameText = (a?: string, b?: string) =>
  (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

export const leadAddress = (lead: ProjectImportLead) =>
  [[lead.street, lead.street2].filter(Boolean).join(' '), lead.city, `${lead.state} ${lead.zipCode}`]
    .filter(Boolean)
    .join(', ');

export const leadFeesTotal = (lead: ProjectImportLead) =>
  (lead.fees || []).reduce((sum, fee) => sum + (Number(fee.amount) || 0), 0);

// Everything the wizard has no field for goes into the permit description so
// the office still sees it while reviewing.
const leadDescription = (lead: ProjectImportLead) => {
  const { requester } = lead;
  const requesterLine = [
    [requester?.name, requester?.role && `(${requester.role})`].filter(Boolean).join(' '),
    requester?.company,
    requester?.email,
    requester?.phone,
  ]
    .filter(Boolean)
    .join(' - ');
  const lines = [
    `Monday lead ${lead.projectNumber}`,
    requesterLine && `Requester: ${requesterLine}`,
    ...(lead.fees || []).map(fee => `Fee: ${fee.description} - $${Number(fee.amount).toFixed(2)}`),
    ...(lead.permits || []).slice(1).map(permit =>
      `Additional permit on this lead: ${permit.name} (${permit.serviceType}, ${permit.work})`),
    lead.folderUrl && `Folder: ${lead.folderUrl}`,
    lead.signedPdfUrl && `Signed approval: ${lead.signedPdfUrl}`,
  ];
  return lines.filter(Boolean).join('\n');
};

/**
 * Add Project wizard values for a Monday lead. Select fields need the same
 * objects their option lists hold, so the vocabularies and managers are
 * fetched and matched here (by title / id) before the wizard opens.
 */
export const buildProjectImportFormValues = async (lead: ProjectImportLead) => {
  const [vocabResponse, managersResponse] = await Promise.all([
    getVocabulariesDataList({ ...GET_ALL_QUERY_PARAMS, ordering: 'title' }),
    getUsersCustom({ userRole: USER_TYPES_VALUES.EMPLOYEE, project: 'true' }),
  ]);
  const vocab = (vocabResponse.data.results || []).filter(item => item?.isActive);
  const ofEntity = (entity: number) => vocab.filter(item => item.vocabularyEntity === entity);
  // get-all-users/ returns a plain array despite the UsersList typing.
  const managers: any[] = ((managersResponse.data as unknown as any[]) || [])
    .filter(user => user?.isActive);

  const permit = lead.permits?.[0];
  const manager = managers.find(user => user.id === lead.assignedEmployee) || '';
  const workScope = ofEntity(VOCAB_WORK).find(item => sameText(item.title, permit?.work)) || '';
  const serviceType = ofEntity(VOCAB_SERVICE_TYPE).filter(item => sameText(item.title, permit?.serviceType));
  const buildingMatchers: Record<string, RegExp> = {
    '1_2_FAMILY': /residential/i,
    MULTIFAMILY: /multi.*family|multifamily/i,
    COMMERCIAL: /commercial/i,
  };
  const building = ofEntity(VOCAB_BUILDING).find(item =>
    buildingMatchers[lead.projectType]?.test(item.title)) || '';
  const total = leadFeesTotal(lead);

  return {
    ...defaultValues,
    address: leadAddress(lead),
    street: [lead.street, lead.street2].filter(Boolean).join(' '),
    city: lead.city,
    state: lead.state,
    zipCode: lead.zipCode,
    name: lead.projectName,
    jurisdiction: lead.jurisdiction ? { id: lead.jurisdiction, name: lead.jurisdictionName } : '',
    manager,
    permitManager: manager,
    project_type: lead.projectType,
    permitName: permit?.name || '',
    description: leadDescription(lead),
    serviceType: serviceType.length ? serviceType : '',
    workScope,
    building,
    inspectionsNumber: permit?.inspections ? String(permit.inspections) : '',
    totalContract: total ? total.toFixed(2) : '',
  };
};

// Monday sends digits with the country code (12395550142); the contact form
// masks phones as 999-999-9999. Anything that isn't a US number is left out.
export const formatLeadPhone = (phone?: string | null) => {
  let digits = (phone || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return '';
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Add Contact form values for the requester as an individual.
export const requesterIndividualValues = (requester: LeadRequester) => {
  const [firstName = '', ...rest] = (requester.name || '').trim().split(/\s+/);
  return {
    firstName,
    lastName: rest.join(' '),
    email: (requester.email || '').trim().toLowerCase(),
    phoneNumber: formatLeadPhone(requester.phone),
  };
};

// Add Contact form values for the requester's company. The email and phone
// Monday sends are the person's, so they are left for the reviewer to fill.
export const requesterCompanyValues = (requester: LeadRequester) => ({
  companyName: (requester.company || '').trim(),
});
